/**
 * `AIGuardrailsModel.ts` — port of Unity's `AIGuardrailsModel.cs` +
 * `.Permission.cs` + `.Interaction.cs` + `.Notice.cs`.
 *
 * Upstream deleted `AIGuardrailsModel.ToolValidation.cs`: argument
 * discipline is prompt-owned ("the runtime no longer second-guesses tool
 * arguments"), and ambiguity resolution goes through multi-round
 * `present_options`. The hard runtime guardrails that remain:
 *
 *   1. Permission — `writerOnly` tools are denied for viewers, EXCEPT when
 *      the caller is workflow playback (see `authorizeToolExecution`).
 *   2. Interaction — `present_options` rounds (max 6 options each, deduped)
 *      + `Listen_User` push-to-talk handoff.
 *   3. Notices — connection/error notices surfaced to the conversation UI.
 */

import type { AIAgentTool } from "@ai/registry/AIAgentToolRegistryModel";
import { TOOL_TRIGGER } from "@ai/registry/AIAgentToolRegistryModel";
import type {
  AIConversationPresentationModel,
  AIPresentOptionsRound,
} from "@ai/interface/AIConversationPresentationModel";

export interface AIGuardrailToolAuthorizationResult {
  readonly isAllowed: boolean;
  /** Denial string returned to the model as the tool output. */
  readonly denialMessage?: string;
}

export type GuardrailNoticeSeverity = "info" | "warning" | "error";

export interface IAIGuardrailsModel {
  authorizeToolExecution(
    tool: AIAgentTool | null,
    toolName: string,
    triggeredBy?: string,
  ): AIGuardrailToolAuthorizationResult;
  tryPresentOptions(rounds: ReadonlyArray<AIPresentOptionsRound>, moreInfoRequired: boolean): string;
  wereOptionsRecentlyPresented(): boolean;
  tryTriggerListenUser(): string;
  publishGuardrailNotice(message: string, severity: GuardrailNoticeSeverity): void;
}

const MAX_OPTIONS_PER_ROUND = 6;
/** Unity's "options recently presented" suppression window. */
const OPTIONS_RECENCY_WINDOW_MS = 3000;

export class AIGuardrailsModel implements IAIGuardrailsModel {
  private readonly m_isWriter: () => boolean;
  private readonly m_presentation: AIConversationPresentationModel;
  private readonly m_requestListenUser: () => boolean;
  private m_lastOptionsPresentedAt = 0;

  constructor(
    isWriter: () => boolean,
    presentation: AIConversationPresentationModel,
    requestListenUser: () => boolean,
  ) {
    this.m_isWriter = isWriter;
    this.m_presentation = presentation;
    this.m_requestListenUser = requestListenUser;
  }

  /**
   * `AIGuardrailsModel.Permission` — the `writerOnly` role gate.
   *
   * ⚠ **Workflow playback is exempt, and that is the whole point of the
   * `triggeredBy` argument.** This gate asks "may the AGENT change the project
   * on this user's behalf?", and a workflow script step is not the agent
   * deciding anything: it is content an AUTHOR wrote into `workflow.yaml`,
   * replayed verbatim. Only a writer can save that file, so the authorization
   * question was already answered — by the author, at authoring time. Held to
   * this gate instead, every scene command in every guided lesson
   * (`hide_parts`, `isolate`, `highlight_part`, `Toggle_Model_Animation`, …)
   * was refused for exactly the audience guided training exists for, and
   * refused SILENTLY: `WorkflowScriptRunner` logs a warning and keeps playing,
   * so the learner saw a lesson narrate steps that never happened.
   *
   * The bypass is deliberately BLANKET rather than scoped to the view-only
   * command vocabulary (`K_SCRIPT_DOCUMENTED_TOOL_IDS`) — user direction,
   * 2026-08-31, "commands in workflow should be able to execute regardless of
   * user role". Scoping was considered and rejected because it re-creates the
   * reported bug class one command over: an author previews a script that
   * works, and it silently does something else for the learner. If a narrower
   * boundary is ever wanted, `isStructuralScriptCommand()` is the ready-made
   * lever — it already names the commands that edit the project rather than
   * the view and that no `revert` can undo.
   *
   * ⚠ **Known consequence, not a defect of this gate:** these tools persist.
   * `hide_parts` writes an entity snapshot to Dynamo and broadcasts
   * `update_entitysnapshot`, so a viewer's playback now mutates the project it
   * is teaching — and the script's own `revert` puts it back the same way.
   * That is how the web port implements every one of these tools; there is no
   * apply-only path. It is pre-existing behaviour that this change makes
   * REACHABLE for viewers, and it is what a cross-account sharee will hit first
   * if the backend refuses their write.
   *
   * An OMITTED `triggeredBy` is never a bypass: the default is the agent's, so
   * a future caller that forgets to say who it is fails closed.
   */
  authorizeToolExecution(
    tool: AIAgentTool | null,
    toolName: string,
    triggeredBy: string = TOOL_TRIGGER.AI,
  ): AIGuardrailToolAuthorizationResult {
    if (triggeredBy === TOOL_TRIGGER.WORKFLOW) return { isAllowed: true };
    if (tool?.writerOnly && !this.m_isWriter()) {
      return {
        isAllowed: false,
        denialMessage:
          `error: permission_denied. The current user is a viewer and '${toolName}' modifies the project. ` +
          "Tell the user they need editor access for this action.",
      };
    }
    return { isAllowed: true };
  }

  tryPresentOptions(
    rounds: ReadonlyArray<AIPresentOptionsRound>,
    moreInfoRequired: boolean,
  ): string {
    const sanitized: AIPresentOptionsRound[] = [];
    for (const round of rounds) {
      const question = round.question?.trim();
      const seen = new Set<string>();
      const options: string[] = [];
      for (const raw of round.options ?? []) {
        const option = raw?.trim();
        if (!option) continue;
        const key = option.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        options.push(option);
        if (options.length >= MAX_OPTIONS_PER_ROUND) break;
      }
      if (!question || options.length < 2) continue;
      sanitized.push({ question, options });
    }

    if (sanitized.length === 0) {
      return "error: present_options_requires_at_least_one_round_with_a_question_and_2_options";
    }

    this.m_presentation.publishPresentOptions(sanitized, moreInfoRequired);
    this.m_lastOptionsPresentedAt = Date.now();
    return "ok: options presented to the user. End your response now; the user's picks arrive as their next message.";
  }

  wereOptionsRecentlyPresented(): boolean {
    return Date.now() - this.m_lastOptionsPresentedAt <= OPTIONS_RECENCY_WINDOW_MS;
  }

  tryTriggerListenUser(): string {
    const triggered = this.m_requestListenUser();
    return triggered
      ? "ok: microphone opened for the user's voice reply"
      : "ok: constant listening is already active";
  }

  publishGuardrailNotice(message: string, severity: GuardrailNoticeSeverity): void {
    this.m_presentation.publishGuardrailNotice(message, severity);
  }
}

export const AI_GUARDRAILS_MODEL = Symbol.for("Campfire.AIGuardrailsModel");
